import React, { forwardRef, useMemo, type ReactNode } from 'react';
import { StyleSheet, type TextInput as RNTextInput } from 'react-native';
import type { TextInputProps as PaperTextInputProps } from 'react-native-paper';
import * as yup from 'yup';
import { TextInput } from './TextInput';

type Props = {
  label: string;
  showLabel?: boolean;
  value: string;
  onChange?: (value: string) => void;
  icon?: ReactNode;
  onIconPress?: () => void;
  error?: boolean;
  validateOnChange?: boolean;
} & Omit<
  PaperTextInputProps,
  'onChangeText' | 'onChange' | 'value' | 'keyboardType' | 'autoCapitalize'
>;

export const EmailInput = forwardRef<RNTextInput, Props>(
  (
    {
      label,
      showLabel = true,
      value,
      onChange,
      icon,
      style,
      disabled,
      onIconPress,
      error,
      validateOnChange = false,
      ...otherProps
    }: Props,
    ref,
  ) => {
    // Email validation
    const isValidEmail = (email: string): boolean => {
      if (!email) return true; // Empty is valid (let required prop handle it)
      return yup.string().email().isValidSync(email);
    };

    const handleChange = (text: string) => {
      if (!onChange) return;
      onChange(text);
    };

    // Show error if:
    // 1. error prop is explicitly set, OR
    // 2. validateOnChange is true AND value is not empty AND email is invalid
    const hasError =
      error || (validateOnChange && value.length > 0 && !isValidEmail(value));

    // Memoize error style to avoid recreation
    const errorStyle = useMemo(
      () => (hasError ? styles.errorInput : undefined),
      [hasError],
    );

    return (
      <TextInput
        ref={ref}
        label={label}
        showLabel={showLabel}
        value={value}
        onChange={handleChange}
        icon={icon}
        onIconPress={onIconPress}
        disabled={disabled}
        keyboardType="email-address"
        autoCapitalize="none"
        autoComplete="email"
        textContentType="emailAddress"
        style={[errorStyle, style]}
        {...otherProps}
      />
    );
  },
);
EmailInput.displayName = 'EmailInput';

const styles = StyleSheet.create({
  errorInput: {
    // borderColor: Colors.pink0,
    // borderWidth: 1,
  },
});
